import React, { useState } from "react";

import {
  ConfirmDialog,
  ConfirmDialogProps,
  Stack,
  Text,
  TextField,
  useTheme
} from "@bps/fluent-ui";
import { dangerButtonStyles } from "@components/buttons/DangerButton";
import { Tenant } from "@libs/api/gateways/plt/plt-gateway.dtos";
import {
  useActivateTenantMutation,
  useDeactivateTenantMutation
} from "@libs/api/gateways/plt/plt-gateway.hooks";

export interface TenantIsInactiveChangeDialogProps
  extends Omit<ConfirmDialogProps, "onConfirm"> {
  tenant: Tenant;
}

export const TenantIsInactiveChangeDialog: React.FC<TenantIsInactiveChangeDialogProps> = ({
  tenant,
  onCancel,
  confirming,
  ...props
}) => {
  const theme = useTheme();
  const [name, setName] = useState<string | undefined>("");
  const [changing, setChanging] = useState<boolean>(false);
  const activateMutation = useActivateTenantMutation();
  const deactivateMutation = useDeactivateTenantMutation();
  const modeMutation = tenant.isInactive
    ? activateMutation
    : deactivateMutation;

  const onConfirm = async () => {
    setChanging(true);
    try {
      await modeMutation.mutateAsync({ tenantId: tenant.id });
      onCancel?.();
    } catch {}
    setChanging(false);
  };

  const modeName = tenant.isInactive ? "Activate" : "Deactivate";

  const title = `Confirm ${modeName} Tenant`;

  return (
    <ConfirmDialog
      dialogContentProps={{
        title
      }}
      confirmButtonProps={{
        text: modeName,
        styles: dangerButtonStyles(theme),
        disabled: name !== tenant.name || changing
      }}
      cancelButtonProps={{ text: "No" }}
      {...props}
      confirming={confirming || changing}
      onConfirm={onConfirm}
      onCancel={onCancel}
    >
      <Stack tokens={{ childrenGap: theme.spacing.m }}>
        <Stack tokens={{ childrenGap: theme.spacing.s2 }}>
          <Text>Please enter the tenant name to confirm:</Text>
          <Text>{tenant.name}</Text>
        </Stack>
        <TextField value={name} onChange={(e, value) => setName(value)} />
      </Stack>
    </ConfirmDialog>
  );
};
